<template>
  <v-row
    no-gutters
    align="center"
    justify="center"
    class="text-caption grey--text text--darken-2 pt-1"
  >
    <span
      class="d-inline-block"
      v-if="finishedAt"
      v-tooltip:bottom="statusTooltip"
    >
      {{ $t('analysis.generateReport.reportDocuments.lastGenerated') }}
      <strong>{{ timeSinceCompletion }}</strong>
      <template v-if="sendStatus === 'PENDING'">, {{ $t('analysis.generateReport.reportDocuments.sending') }}...</template>
      <template v-else-if="sendStatus === 'PARTIAL'">,
        <span class="warning--text">
          {{ $t('analysis.generateReport.reportDocuments.partiallyFailedToSend') }}
          <template v-if="failedRecipientNames.length"> {{ failedRecipientNames.join(', ') }}</template>
          <template v-if="timeSinceSent"> <strong> {{ timeSinceSent }}</strong></template>.
        </span>
      </template>
      <template v-else-if="sendStatus === 'FAILED'">,
        <span class="warning--text">
          {{ $t('analysis.generateReport.reportDocuments.failedToSend') }}
          <template v-if="timeSinceSent"> <strong>{{ timeSinceSent }}</strong></template>.
        </span>
      </template>
      <template v-else-if="lastSentAt">,
        {{ $t('analysis.generateReport.reportDocuments.sent') }} <strong>{{ timeSinceSent }}</strong>.
      </template>
    </span>
    <span
      class="d-inline-block warning--text"
      v-else-if="failedAt"
      v-tooltip:bottom="displayFailedAt"
    >
      {{ $t('analysis.generateReport.reportDocuments.processingFailed') }}
    </span>
    <span
      class="d-inline-block"
      v-else-if="startedProcessingAt"
      v-tooltip:bottom="displayStartedProcessingAt"
    >
      {{ $t('analysis.generateReport.reportDocuments.processing') }}...
    </span>
    <span
      class="d-inline-block"
      v-else-if="displayQueuedAt"
      v-tooltip:bottom="displayQueuedAt"
    >
      {{ $t('analysis.generateReport.reportDocuments.queued') }}...
    </span>
    <div
      v-else
      class="d-inline-block pr-1"
    >
      <platform-spinner color="grey" :size="12" :width="2" />
    </div>
  </v-row>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import dayjs from 'dayjs'
import { ReportsIndividual } from '@betterboards/shared/types/Individual'
import PlatformSpinner from '@/components/shared/PlatformSpinner.vue'
import { LongDateTimeDisplayFormat } from '@/components/PlatformDateDisplay.vue'
import { ReportSendStatus } from '@/API'

interface SendRecipientStatus {
  sendStatus?: ReportSendStatus.PENDING | ReportSendStatus.SENT | ReportSendStatus.FAILED
}

type SendRecipientsByIndividualId = Record<string, SendRecipientStatus>

export default Vue.extend({
  name: 'ReportDocumentStatus',
  components: {
    PlatformSpinner
  },
  props: {
    finishedAt: {
      type: String,
      required: false
    },
    startedProcessingAt: {
      type: String,
      required: false
    },
    queuedAt: {
      type: String,
      required: false
    },
    failedAt: {
      type: String,
      required: false
    },
    lastSentAt: {
      type: String as PropType<string | null>,
      required: false
    },
    sendStatus: {
      type: String as PropType<ReportSendStatus.PENDING | ReportSendStatus.SENT | ReportSendStatus.FAILED | ReportSendStatus.PARTIAL>,
      required: false
    },
    sendRecipients: {
      type: String as PropType<string | null>,
      required: false
    },
    individuals: {
      type: Array as PropType<ReportsIndividual[]>,
      required: false
    }
  },
  computed: {
    timeSinceCompletion (): string | undefined {
      if (this.finishedAt) {
        return dayjs(this.finishedAt).fromNow()
      }
      return undefined
    },
    displayFinishedAt (): string | undefined {
      if (this.finishedAt) {
        return this.$t(
          'analysis.generateReport.reportDocuments.status.generated',
          [dayjs(this.finishedAt).format(LongDateTimeDisplayFormat)]) as string
      }
      return undefined
    },
    displayStartedProcessingAt (): string | undefined {
      if (this.startedProcessingAt) {
        return this.$t(
          'analysis.generateReport.reportDocuments.status.startedProcessingAt',
          [dayjs(this.startedProcessingAt).format(LongDateTimeDisplayFormat)]) as string
      }
      return undefined
    },
    displayQueuedAt (): string | undefined {
      if (this.queuedAt) {
        return this.$t(
          'analysis.generateReport.reportDocuments.status.queuedAt',
          [dayjs(this.queuedAt).format(LongDateTimeDisplayFormat)]) as string
      }
      return undefined
    },
    displayFailedAt (): string | undefined {
      if (!this.failedAt) {
        return undefined
      }
      const displayFailedAt = dayjs(this.failedAt).format(LongDateTimeDisplayFormat)
      return this.$t(
        'analysis.generateReport.reportDocuments.status.failedAt',
        [displayFailedAt]
      ) as string
    },
    timeSinceSent (): string | undefined {
      if (this.lastSentAt) {
        return dayjs(this.lastSentAt).fromNow()
      }
      return undefined
    },
    displayLastSentAt (): string | undefined {
      if (this.lastSentAt) {
        return this.$t(
          'analysis.generateReport.reportDocuments.status.sentAt',
          [dayjs(this.lastSentAt).format(LongDateTimeDisplayFormat)]) as string
      }
      return undefined
    },
    sendRecipientsMap (): SendRecipientsByIndividualId {
      if (!this.sendRecipients) {
        return {}
      }
      try {
        return JSON.parse(this.sendRecipients) as SendRecipientsByIndividualId
      } catch (err) {
        console.warn('Failed to parse sendRecipients JSON', err)
        return {}
      }
    },
    failedRecipientIds (): string[] {
      return Object.entries(this.sendRecipientsMap)
        .filter(([individualId, recipientState]: [string, SendRecipientStatus]) => {
          return individualId.length > 0 && recipientState.sendStatus === ReportSendStatus.FAILED
        })
        .map(([individualId]) => {
          return individualId
        })
    },
    sentRecipientCount (): number {
      return Object.entries(this.sendRecipientsMap)
        .filter(([individualId, recipientState]: [string, SendRecipientStatus]) => {
          return individualId.length > 0 && recipientState.sendStatus === ReportSendStatus.SENT
        })
        .length
    },
    failedRecipientCount (): number {
      return this.failedRecipientIds.length
    },
    failedRecipientNames (): string[] {
      if (!this.individuals || this.individuals.length === 0) {
        return []
      }

      return this.failedRecipientIds
        .map((individualId) => {
          const individual: ReportsIndividual | undefined = this.individuals.find(
            (individual: ReportsIndividual) => individual.id === individualId
          )
          return individual?.preferredName
        })
        .filter((preferredName): preferredName is string => {
          return Boolean(preferredName)
        })
    },
    failedRecipientsTooltip (): string | undefined {
      if (!this.failedRecipientNames.length) {
        return undefined
      }

      return this.$t(
        'analysis.generateReport.reportDocuments.failedRecipientsTooltip',
        [this.failedRecipientNames.join(', ')]
      ) as string
    },
    statusTooltip (): string | undefined {
      const parts: string[] = []
      if (this.displayFinishedAt) {
        parts.push(this.displayFinishedAt)
      }
      if (this.displayLastSentAt) {
        parts.push(this.displayLastSentAt)
      }
      if (this.failedRecipientsTooltip) {
        parts.push(this.failedRecipientsTooltip)
      }
      return parts.length > 0 ? parts.join(' ') : undefined
    }
  }
})
</script>
